import { useCallback, useEffect, useRef, useState } from "react";
import { createVitals } from "../services/v5Api";
import {
  createVitalsRequestId,
  getVitalsResultValidationError,
  isCompatibleVitalsCapability,
  parseVitalsNativeMessage,
  postVitalsWebViewMessage,
} from "../utlis/vitalsBridge";

const vitalsDebug = (message, detail) => {
  if (process.env.NODE_ENV === "production") return;
  if (detail === undefined) {
    console.debug(message);
    return;
  }
  console.debug(message, detail);
};

const getApiErrorDetail = (error) => {
  const data = error?.response?.data;
  const directMessage = [
    typeof data === "string" ? data : null,
    data?.message,
    data?.detail,
    data?.reason,
    typeof data?.error === "string" ? data.error : null,
  ].find((value) => typeof value === "string" && value.trim());
  if (directMessage) return directMessage.trim();

  if (Array.isArray(data?.errors)) {
    const messages = data.errors
      .map((value) =>
        typeof value === "string" ? value : value?.message || value?.reason,
      )
      .filter(Boolean);
    if (messages.length) return messages.join("; ");
  }

  if (data?.errors && typeof data.errors === "object") {
    const messages = Object.entries(data.errors)
      .map(([field, value]) => {
        const detail = Array.isArray(value) ? value.join(", ") : String(value);
        return `${field}: ${detail}`;
      })
      .filter(Boolean);
    if (messages.length) return messages.join("; ");
  }

  return (
    (typeof error?.message === "string" && error.message.trim()) ||
    "The vitals API did not provide a failure reason."
  );
};

const getVitalsSaveFailure = (error) => {
  const statusCode = error?.response?.status || null;
  const detail = getApiErrorDetail(error);
  return {
    code: "VITALS_SAVE_FAILED",
    reason: statusCode ? `${detail} (HTTP ${statusCode})` : detail,
    statusCode,
    recoverable: true,
  };
};

const useVitalsListener = ({ seniorId, onSuccess, onError, onCancelled }) => {
  const [capability, setCapability] = useState(null);
  const [isCaptureActive, setIsCaptureActive] = useState(false);
  const activeRequestRef = useRef(null);
  const resultInFlightRef = useRef(false);
  const savedResultsRef = useRef(new Map());

  const clearActiveRequest = useCallback(() => {
    activeRequestRef.current = null;
    resultInFlightRef.current = false;
    setIsCaptureActive(false);
  }, []);

  useEffect(() => {
    const handleMessage = async (event) => {
      const isCachedCapability = event.__inochiCachedCapability === true;
      if (
        !isCachedCapability &&
        (event.source !== window || event.origin !== window.location.origin)
      ) {
        return;
      }

      const message = parseVitalsNativeMessage(event.data);
      if (!message) return;

      if (message.type === "NATIVE_CAPABILITIES") {
        setCapability({
          bridgeSessionId: message.bridgeSessionId,
          ...message.payload?.vitals,
          compatible: isCompatibleVitalsCapability(message),
        });
        if (
          activeRequestRef.current &&
          activeRequestRef.current.bridgeSessionId !== message.bridgeSessionId
        ) {
          clearActiveRequest();
        }
        return;
      }

      const active = activeRequestRef.current;
      if (
        !active ||
        message.bridgeSessionId !== active.bridgeSessionId ||
        message.requestId !== active.requestId ||
        message.seniorId !== active.seniorId
      ) {
        return;
      }

      if (message.type === "VITALS_CAPTURE_ACCEPTED") {
        setIsCaptureActive(true);
        return;
      }

      if (message.type === "VITALS_CANCELLED") {
        clearActiveRequest();
        onCancelled?.(message.payload);
        return;
      }

      if (message.type === "VITALS_ERROR") {
        clearActiveRequest();
        onError?.(message.payload);
        return;
      }

      if (message.type !== "VITALS_RESULT" || resultInFlightRef.current) {
        return;
      }

      if (savedResultsRef.current.has(active.requestId)) {
        const ackSent = postVitalsWebViewMessage({
          type: "VITALS_RESULT_ACK",
          bridgeSessionId: active.bridgeSessionId,
          requestId: active.requestId,
          seniorId: active.seniorId,
          payload: {
            status: "saved",
            recordId: savedResultsRef.current.get(active.requestId),
          },
        });
        if (ackSent) {
          clearActiveRequest();
        } else {
          resultInFlightRef.current = false;
        }
        return;
      }

      const validationError = getVitalsResultValidationError(message);
      if (validationError) {
        vitalsDebug("[vitals] rejected result before save", {
          code: validationError.code,
        });
        postVitalsWebViewMessage({
          type: "VITALS_RESULT_ACK",
          bridgeSessionId: active.bridgeSessionId,
          requestId: active.requestId,
          seniorId: active.seniorId,
          payload: { status: "failed", ...validationError },
        });
        clearActiveRequest();
        onError?.(validationError);
        return;
      }

      const metrics = message.payload.metrics;
      const vitals = {
        hr: metrics.hr,
        sbp: metrics.sbp,
        dbp: metrics.dbp,
        spo2: metrics.spo2,
        rr: metrics.rr,
      };
      if (Number.isFinite(metrics.hrv_sdnn)) vitals.hrv_sdnn = metrics.hrv_sdnn;
      if (Number.isFinite(metrics.stress)) vitals.stress = metrics.stress;
      if (Number.isFinite(metrics.wellness)) vitals.wellness = metrics.wellness;
      const payload = {
        timestamp: message.payload.capturedAt,
        requestId: active.requestId,
        schemaVersion: 1,
        sdkVersion: message.payload.sdkVersion,
        vitals,
      };

      postVitalsWebViewMessage({
        type: "VITALS_RESULT_ACK",
        bridgeSessionId: active.bridgeSessionId,
        requestId: active.requestId,
        seniorId: active.seniorId,
        payload: { status: "received" },
      });
      resultInFlightRef.current = true;

      try {
        vitalsDebug("[vitals] createVitals request");
        const response = await createVitals(payload, active.seniorId, {
          requestId: active.requestId,
        });
        vitalsDebug("[vitals] createVitals success");
        const recordId = response?.id || response?.data?.id || null;
        savedResultsRef.current.set(active.requestId, recordId);
        const ackSent = postVitalsWebViewMessage({
          type: "VITALS_RESULT_ACK",
          bridgeSessionId: active.bridgeSessionId,
          requestId: active.requestId,
          seniorId: active.seniorId,
          payload: {
            status: "saved",
            recordId,
          },
        });
        if (ackSent) {
          clearActiveRequest();
        } else {
          resultInFlightRef.current = false;
        }
        onSuccess?.({ response, vitals });
      } catch (error) {
        resultInFlightRef.current = false;
        const failure = getVitalsSaveFailure(error);
        vitalsDebug("[vitals] createVitals failed", {
          status: error?.response?.status,
          code: failure.code,
        });
        postVitalsWebViewMessage({
          type: "VITALS_RESULT_ACK",
          bridgeSessionId: active.bridgeSessionId,
          requestId: active.requestId,
          seniorId: active.seniorId,
          payload: {
            status: "failed",
            ...failure,
          },
        });
        onError?.(failure);
      }
    };

    window.addEventListener("message", handleMessage);
    if (window.__inochiVitalsCapabilities) {
      handleMessage({
        data: window.__inochiVitalsCapabilities,
        __inochiCachedCapability: true,
      });
    }
    return () => window.removeEventListener("message", handleMessage);
  }, [clearActiveRequest, onCancelled, onError, onSuccess]);

  const startCapture = useCallback(
    ({ subjectInfo, locale, initialVitals = null }) => {
      if (!capability?.compatible || !capability.bridgeSessionId || !seniorId) {
        return { started: false, reason: "sdk_unavailable" };
      }
      if (activeRequestRef.current) return { started: false, reason: "busy" };

      const request = {
        bridgeSessionId: capability.bridgeSessionId,
        requestId: createVitalsRequestId(),
        seniorId: String(seniorId),
      };
      const sent = postVitalsWebViewMessage({
        type: "START_VITALS_TEST",
        ...request,
        payload: {
          subjectInfo,
          locale,
          ...(initialVitals ? { initialVitals } : {}),
        },
      });
      if (!sent) return { started: false, reason: "bridge_unavailable" };

      activeRequestRef.current = request;
      setIsCaptureActive(true);
      return { started: true, requestId: request.requestId };
    },
    [capability, seniorId],
  );

  return {
    capability,
    isCaptureActive,
    startCapture,
  };
};

export default useVitalsListener;
